import React, { useEffect, useState } from "react";

import {
  ArrowRightAlt,
  Edit,
  TextSnippetOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";

import { Input } from "../../../common";
import { getRoles as getRolesAction } from "../../../redux/slices/companySlice";
import { connect } from "react-redux";

/* ============================================================================
  AssessmentFormHeader
============================================================================= */
const AssessmentFormHeader = ({
  onSubmit,
  onCancel,
  getRoles,
  roles,
  edit,
}) => {
  const {
    control,
    trigger,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useFormContext();
  const testsCount = getValues("tests")?.length || 0;
  const assessment_time = getValues("assessment_time") || null;
  const [editName, setEditName] = useState(false);

  const _handleToggleEditName = (value) => {
    if (editName && !value) return trigger("assessment_name");
    setEditName((prev) => !prev);
  };

  useEffect(() => {
    getRoles();
  }, []);

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems="center"
      bgcolor="secondary.main"
      px={3}
      py={2}
      borderRadius="8px"
    >
      <Box display="flex" flexDirection="column" gap={1} width="80%" mr={3}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          width="100%"
        >
          {/* Assessment Name Field */}
          <Controller
            name="assessment_name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Box display="flex" alignItems="center" gap={2}>
                {editName ? (
                  <Input
                    size="small"
                    variant="standard"
                    sx={{ input: { color: "white", fontSize: "20px" } }}
                    error={!!error?.message}
                    {...field}
                  />
                ) : (
                  <Box>
                    <Typography
                      color="white"
                      fontWeight={600}
                      maxWidth="550px"
                      noWrap
                      fontSize="20px"
                    >
                      {field?.value || "--"}
                    </Typography>
                    {error?.message && (
                      <Typography fontSize="13px" color="#d32f2f">
                        {error?.message}
                      </Typography>
                    )}
                  </Box>
                )}
                {editName ? (
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: "white",
                      color: "primary.main",
                      borderRadius: "40px",
                      fontSize: "18px",
                    }}
                    onClick={() => _handleToggleEditName(field?.value)}
                  >
                    Save
                  </Button>
                ) : (
                  <IconButton
                    onClick={() => _handleToggleEditName(field?.value)}
                  >
                    <Edit sx={{ color: "white" }} />
                  </IconButton>
                )}
              </Box>
            )}
          />

          {/* Role Selection with Autocomplete */}
          <Controller
            name="role"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                size="small"
                disabled={edit}
                disablePortal
                options={roles?.length > 0 ? roles : []}
                sx={{ width: 170, mt: 1 }}
                // value={field.value}
                value={field.value || null}
                onChange={(_, newValue) => field.onChange(newValue || "")}
                inputValue={field.value}
                onInputChange={(event, newInputValue) =>
                  field.onChange(newInputValue)
                }
                renderInput={(params) => (
                  <>
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Select a role"
                      sx={{
                        borderRadius: "8px",
                        color: "white",
                        mt: 0.5,
                        fontSize: "18px",
                        fontWeight: "bold",
                        "& .MuiInputBase-input": {
                          color: "white", // Input text color
                        },
                        "& .MuiInputLabel-root": {
                          color: "white", // Label color
                        },
                        "& .MuiSvgIcon-root": {
                          color: "white", // Dropdown arrow color
                        },
                        "& .MuiAutocomplete-clearIndicator": {
                          color: "white", // Clear icon color
                        },
                      }}
                    />
                    {error?.message && (
                      <Typography fontSize="13px" color="#d32f2f">
                        {error?.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            )}
          />
        </Box>

        {/* Tests Count & Assessment Time */}
        {(testsCount || assessment_time) && (
          <Box display="flex" gap={2} mt={0.5}>
            {testsCount && (
              <Box display="flex" alignItems="center" gap={1}>
                <TextSnippetOutlined
                  sx={{ color: "white", fontSize: "22px" }}
                />
                <Typography color="white">{`${testsCount} tests`}</Typography>
              </Box>
            )}
            {assessment_time && (
              <Box display="flex" alignItems="center" gap={1}>
                <TimerOutlined sx={{ color: "white", fontSize: "22px" }} />
                <Typography color="white">{assessment_time || "--"}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        alignItems="center"
        gap={2}
      >
        <Button
          type="button"
          variant="contained"
          disabled={isSubmitting}
          endIcon={<KeyboardReturnIcon sx={{ color: "secondary.main" }} />}
          sx={{
            bgcolor: "white",
            color: "secondary.main",
            borderRadius: "40px",
            ":disabled": { bgcolor: "#999", color: "white" },
            width: { xs: "100%", md: "auto" },
          }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          endIcon={
            isSubmitting ? (
              <CircularProgress size={18} sx={{ color: "white" }} />
            ) : (
              <ArrowRightAlt sx={{ color: "secondary.main" }} />
            )
          }
          sx={{
            bgcolor: "white",
            color: "secondary.main",
            borderRadius: "40px",
            ":disabled": { bgcolor: "#999", color: "white" },
            width: { xs: "100%", md: "auto" },
          }}
          onClick={handleSubmit(onSubmit)}
        >
          Save and continue
        </Button>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  roles: state.company?.roles,
});

const mapDipatchToProps = {
  getRoles: getRolesAction,
};
/* Export
============================================================================= */

export default React.memo(
  connect(mapStateToProps, mapDipatchToProps)(AssessmentFormHeader)
);
